import { takeLatest, put, call, select } from "redux-saga/effects";
import React from "react";
import { toast } from 'react-toastify';
import SVG from 'react-inlinesvg';
import { checkCircleSVG, errorCircleSVG, infoSVG } from '../../shared/helpers/icons';
import styles from '../../shared/styles/notifications.module.scss';
import {
    GET_VEHICLE_REQUEST,
    GET_VEHICLES_REQUEST,
    getVehicleSuccess,
    getVehiclesSuccess,
    getVehiclesFailure,
    GET_VEHICLE_AUDIT_REQUEST,
    getVehicleAuditSuccess,
    getVehicleAuditFailure,
    GET_VEHICLE_TICKETS_REQUEST,
    getVehicleTicketsSuccess,
    getVehicleTicketsFailure,
    PATCH_VEHICLE_REQUEST,
    patchVehicleSuccess,
    patchVehicleFailure,
    ASSIGN_VEHICLE_AGENT_REQUEST,
    assignVehicleAgentSuccess,
    assignVehicleAgentFailure,
    getVehicleRequest,
    ADD_VEHICLE_COMMENT_REQUEST,
    addVehicleCommentSuccess,
    addVehicleCommentFailure,
    getVehicleAuditRequest
} from "../actions/vehicles";
import { getVehicles, getVehicleAudit, getVehicleTickets, patchVehicle, assignVehicleAgent, addVehicleComment } from "../services/vehicles";
import { showGlobalError } from "../../app/actions/app";
import { accessTokenSelector } from "../../auth/selectors/token";
import { buildVehicle } from '../helpers/vehicles';
const getVehicleWorker = function* ({ payload: { filters } }) {
    try {

        const token = yield select(accessTokenSelector);

        const {
            data: { vehicles }
        } = yield call(getVehicles, filters, token);

        yield put(getVehicleSuccess(
            vehicles.length ? buildVehicle(vehicles[0]) : {}
        ));
    } catch (err) {
        yield put(getVehiclesFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getVehiclesWorker = function* ({ payload: { filters = {}, page = 1 } }) {
    try {
        const token = yield select(accessTokenSelector);
        if (!token) {
            throw new Error('No authentication token available');
        }

        const response = yield call(getVehicles, filters, token);

        const {
            data: { vehicles, total }
        } = response;

        yield put(getVehiclesSuccess(vehicles || [], total || 0, page, filters));
    } catch (err) {

        yield put(getVehiclesFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getVehicleAuditWorker = function* ({ payload: { vehicleId } }) {
    try {
      const token = yield select(accessTokenSelector);
      if (!token) throw new Error("No authentication token available");
  
      const { data } = yield call(getVehicleAudit, vehicleId, token);
  
      yield put(getVehicleAuditSuccess(data.audits || [], data.pagination || {}));
    } catch (err) {
      yield put(getVehicleAuditFailure(err));
      yield put(showGlobalError(true, err));
    }
  };

  const addVehicleCommentWorker = function* ({ payload: { vehicleId, comments } }) {
    try {
      const token = yield select(accessTokenSelector);
      if (!token) throw new Error("No authentication token available");
      if (!vehicleId || !comments || !String(comments).trim()) {
        throw new Error("Missing vehicleId or comments");
      }

      const response = yield call(addVehicleComment, vehicleId, String(comments).trim(), token);

      yield put(addVehicleCommentSuccess(response.data));

      toast.success(
        <div className={styles.customToastSuccess}>
          <div className={styles.toastContent}>
            <SVG src={checkCircleSVG} className={styles.toastIconSuccess} />
            Comentario guardado con éxito
          </div>
        </div>,
        {
          className: styles.toast,
          toastId: `vehicle-comment-added-${vehicleId}-${Date.now()}`
        }
      );

      yield put(getVehicleAuditRequest(vehicleId));
      yield put(getVehicleRequest({ identifier: vehicleId, full_owner: 1 }));
    } catch (err) {
      yield put(addVehicleCommentFailure(err));
      yield put(showGlobalError(true, err));
    }
  };

  const assignVehicleAgentWorker = function* ({ payload: { vehicleId, agentId } }) {
    try {
      const token = yield select(accessTokenSelector);
      if (!token) throw new Error("No authentication token available");
      if (!vehicleId || !agentId) throw new Error("Missing vehicleId or agentId");

      const response = yield call(assignVehicleAgent, vehicleId, agentId, token);

      yield put(assignVehicleAgentSuccess(response.data));

      toast.success(
        <div className={styles.customToastSuccess}>
          <div className={styles.toastContent}>
            <SVG src={checkCircleSVG} className={styles.toastIconSuccess} />
            Agente asignado con éxito
          </div>
        </div>,
        {
          className: styles.toast,
          toastId: `vehicle-agent-assigned-${vehicleId}-${agentId}`
        }
      );

      yield put(getVehicleRequest({ identifier: vehicleId, full_owner: 1 }));
    } catch (err) {
      yield put(assignVehicleAgentFailure(err));
      
      // Check if it's the specific "Vehicle must have a assigned_plan" error
      const errorMessage = err?.response?.data?.error || err?.message || '';
      
      if (errorMessage.includes('Vehicle must have a assigned_plan')) {
        toast.error(
          <div className={styles.customToastError}>
            <div className={styles.toastContent}>
              <SVG src={errorCircleSVG} className={styles.toastIconError} />
              Vehículo sin plan. No se puede asignar agente.
            </div>
          </div>,
          {
            className: styles.toast,
            toastId: `vehicle-no-plan-${vehicleId}-${Date.now()}`
          }
        );
      } else {
        yield put(showGlobalError(true, err));
      }
    }
  };
  
  const getVehicleTicketsWorker = function* ({ payload: { vehicleId } }) {
    try {
      const token = yield select(accessTokenSelector);
      if (!token) throw new Error("No authentication token available");
  
      const { data } = yield call(getVehicleTickets, vehicleId, token);
  
      yield put(getVehicleTicketsSuccess(data.tickets || [], data.pagination || {}));
    } catch (err) {
      yield put(getVehicleTicketsFailure(err));
      yield put(showGlobalError(true, err));
    }
  };

  const patchVehicleWorker = function* ({ payload: { vehicleId, data } }) {
    try {
      const token = yield select(accessTokenSelector);
      if (!token) throw new Error("No authentication token available");
  
      const response = yield call(patchVehicle, vehicleId, data, token);
  
      yield put(patchVehicleSuccess(response.data));
      toast.success(
        <div className={styles.customToastSuccess}>
            <div className={styles.toastContent}>
                <SVG src={checkCircleSVG} className={styles.toastIconSuccess} />
               Vehículo actualizado con éxito
            </div>
        </div>,
        {
            className: styles.toast,
            toastId: `vehicle-updated-${Date.now()}`
        }
    );
    // const currentFilters = yield select(
    //     (state) => state.vehicles?.lastFilters || {}
    //   );
  
    //   yield put(GET_VEHICLES_REQUEST(currentFilters));

    } catch (err) {
      yield put(patchVehicleFailure(err));
      
      // Check if it's a duplicate plate error
      const errorMessage = err?.response?.data?.error || err?.message || 'Error al actualizar vehículo';
      
      if (errorMessage.includes('Ya existe un vehículo con la patente')) {
        toast.error(
          <div className={styles.customToastError}>
              <div className={styles.toastContent}>
                  <SVG src={errorCircleSVG} className={styles.toastIconError} />
                  {errorMessage}
              </div>
          </div>,
          {
              className: styles.toast,
              toastId: `vehicle-error-${Date.now()}`
          }
        );
      } else {
        yield put(showGlobalError(true, err));
      }
      
    }
  };
  

export default [
    takeLatest(GET_VEHICLE_REQUEST, getVehicleWorker),
    takeLatest(GET_VEHICLES_REQUEST, getVehiclesWorker),
    takeLatest(GET_VEHICLE_AUDIT_REQUEST, getVehicleAuditWorker),
    takeLatest(GET_VEHICLE_TICKETS_REQUEST, getVehicleTicketsWorker),
    takeLatest(PATCH_VEHICLE_REQUEST, patchVehicleWorker),
    takeLatest(ASSIGN_VEHICLE_AGENT_REQUEST, assignVehicleAgentWorker),
    takeLatest(ADD_VEHICLE_COMMENT_REQUEST, addVehicleCommentWorker)
];
